import React, { createContext, useContext, useRef, useState } from "react";
import type { StaffReportDraftListItem, StaffReportStage, StaffReportWorkspaceState } from "@/api/staff_reports";

export type StaffReportDraftData = {
  id: number;
  name: string;
  markdown: string | null;
  facts?: string;
  municipality_id: number;
  staff_report_template_id?: number | null;
  staff_report_research_set_id?: number | null;
  staff_report_run_id?: number | null;
  stage?: StaffReportStage;
  workspace_state?: StaffReportWorkspaceState;
  updated_at: string;
};

type Ctx = {
  workspaceView: "history" | "report";
  setWorkspaceView: (view: "history" | "report") => void;
  selectedDraft: StaffReportDraftData | null;
  setSelectedDraft: (d: StaffReportDraftData | null) => void;
  drafts: StaffReportDraftListItem[];
  setDrafts: React.Dispatch<React.SetStateAction<StaffReportDraftListItem[]>>;
  nextDraftSelectionVersion: () => number;
  getDraftSelectionVersion: () => number;
  beforeLeaveReportRef: React.MutableRefObject<((remember?: boolean) => Promise<boolean>) | null>;
};

const StaffReportsContext = createContext<Ctx | null>(null);

export function StaffReportsProvider({ children }: { children: React.ReactNode }) {
  const [workspaceView, setWorkspaceView] = useState<"history" | "report">("history");
  const [selectedDraft, setSelectedDraftState] = useState<StaffReportDraftData | null>(null);
  const [drafts, setDrafts] = useState<StaffReportDraftListItem[]>([]);
  const draftSelectionVersionRef = useRef(0);
  const beforeLeaveReportRef = useRef<((remember?: boolean) => Promise<boolean>) | null>(null);
  const setSelectedDraft = (draft: StaffReportDraftData | null) => {
    setSelectedDraftState(draft);
    if (draft) setWorkspaceView("report");
  };
  const nextDraftSelectionVersion = () => {
    draftSelectionVersionRef.current += 1;
    return draftSelectionVersionRef.current;
  };
  const getDraftSelectionVersion = () => draftSelectionVersionRef.current;
  return (
    <StaffReportsContext.Provider value={{ workspaceView, setWorkspaceView, selectedDraft, setSelectedDraft, drafts, setDrafts, nextDraftSelectionVersion, getDraftSelectionVersion, beforeLeaveReportRef }}>
      {children}
    </StaffReportsContext.Provider>
  );
}

export function useStaffReports() {
  const ctx = useContext(StaffReportsContext);
  if (!ctx) throw new Error("useStaffReports must be used within StaffReportsProvider");
  return ctx;
}
